import { Button, Heading } from '@appsumo/dorado-react';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { Breadcrumb, Breadcrumbs } from '~/components/ui';
import { DealCommentSearch, DealComments, PricingCard } from '../product';
import DealRatings from './sidebar/DealRatings';
import ProductDetailRating from './sidebar/ProductDetailRating';
import ProductDetails from './sidebar/ProductDetails';

import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { useUserStatus } from '~/hooks/userStatus';
import { DEAL_COMMENT_TYPE, DISCUSSION } from '~/lib/discussions/constants';
import { DiscussionSortOption, DiscussionType } from '~/lib/discussions/types';
import useManualPagination from '~/lib/discussions/useDiscussionManualPagination';
import { useEvent } from '~/lib/events';
import { useExperiment } from '~/lib/experiment';
import { PDPContext } from '~/lib/product/context';
import { getPlanTypeText } from '~/lib/product/utils';
import useUser from '~/lib/user';
import { getQueryFromPath, sentenceCase } from '~/lib/util';
import {
  LEAVE_REVIEW_PAGE_EXPERIMENT_NAME,
  QUESTIONS_2_5_UI_EXPERIMENT_NAME,
  REVIEWS_2_5_UI_EXPERIMENT_NAME,
} from '~/lib/util/constants';
import { Deal } from '~/types/deal';
import AskSumoBox from './AskSumoBox';

interface IAggregateProps {
  deal: Deal;
  title: string;
  discussionType: DiscussionType;
  commentType: DEAL_COMMENT_TYPE;
  sortOptions: DiscussionSortOption[];
}

function AggregateLayout({
  deal,
  title,
  discussionType,
  commentType,
  sortOptions,
}: Readonly<IAggregateProps>) {
  const router = useRouter();
  const { user } = useUser();
  const { userStatus } = useUserStatus(deal.id);
  const contextValue = useMemo(
    () => ({ deal, user, userStatus }),
    [deal, user, userStatus],
  );

  const [commentQuery, setCommentQuery] = useState('');
  const [discussionTotal, setDiscussionTotal] = useState<number>(0);
  const [useNewUI, setUseNewUI] = useState(false);

  const args = {
    name:
      discussionType === DISCUSSION.REVIEWS
        ? REVIEWS_2_5_UI_EXPERIMENT_NAME
        : QUESTIONS_2_5_UI_EXPERIMENT_NAME,
    skipEnroll: false,
    fallbackData: undefined,
    revalidateIfStale: true,
  };

  const { variant: newUIVariant } = useExperiment(
    args.name,
    args.skipEnroll,
    args.fallbackData,
    args.revalidateIfStale,
  );

  const { variant: leaveReviewVariant } = useExperiment(
    LEAVE_REVIEW_PAGE_EXPERIMENT_NAME,
  );

  const isLeaveReviewPageEnable = leaveReviewVariant === 'enabled';

  useEvent('cart:added', () => router.push('/cart/'));

  useEvent('scroll:pricePlanSection', () => {
    router.push(`/products/${deal.slug}/#pricePlans`);
  });

  useIsomorphicLayoutEffect(() => {
    // if querying for comments, don't set total
    if (commentQuery) {
      return;
    }

    const count =
      discussionType === DISCUSSION.REVIEWS
        ? deal?.deal_review.review_count
        : deal?.deal_comment.comment_count;

    setDiscussionTotal(count || 0);
  }, [deal, commentQuery]);

  const pageTitle = `${deal.public_name} ${title}`;
  const PDPUrl = `/products/${deal.slug}/?ref=${discussionType}`;

  const items: Breadcrumb[] = useMemo(
    () => [{ href: PDPUrl, label: deal.public_name }, { label: pageTitle }],
    [PDPUrl, deal.public_name, pageTitle],
  );

  const planTypeText = useMemo(
    () => sentenceCase(getPlanTypeText(deal?.unique_plan_types)),
    [deal],
  );

  // preset query search from url
  useEffect(() => {
    const params = getQueryFromPath(router.asPath);

    if (params.has('search')) {
      setCommentQuery(params.get('search') as string);
    }
  }, [router.asPath]);

  useEffect(() => {
    setUseNewUI(newUIVariant === 'enabled');
  }, [newUIVariant]);

  return (
    <PDPContext.Provider value={contextValue}>
      <div className="mx-auto w-full max-w-7xl grow p-4 sm:pt-0 md:my-12">
        <Breadcrumbs items={items} className="mx-auto mb-4 max-w-6xl lg:mb-6" />

        <article className="mx-auto flex max-w-6xl gap-x-8 max-sm:flex-col">
          <div className="w-full space-y-4 lg:max-w-[calc(100%-432px)]">
            <div>
              <Heading.H1 className="max-sm:text-2xl">{pageTitle}</Heading.H1>

              <div className="mt-2 md:mt-4 lg:hidden">
                <ProductDetailRating
                  dealReview={deal.deal_review}
                  reviewUrl={`/products/${deal.slug}/reviews`}
                  PDPUrl={PDPUrl}
                />
              </div>
            </div>

            {discussionType === DISCUSSION.REVIEWS && deal.reviews_summary && (
              <AskSumoBox summary={deal.reviews_summary} />
            )}

            <div>
              <DealCommentSearch
                query={commentQuery}
                onChange={(query) => setCommentQuery(query)}
                placeholder={`Search ${discussionType}`}
                enableParamsState
              />

              <div className="flex flex-col gap-y-5 py-4">
                <DealComments
                  dealId={deal.id}
                  fallbackData={[]}
                  type={commentType}
                  sortOptions={sortOptions}
                  total={discussionTotal}
                  updateTotal={setDiscussionTotal}
                  query={commentQuery}
                  enableParamsState
                  enableManualPagination
                  paginationHook={useManualPagination}
                />
              </div>
            </div>
          </div>

          <aside
            className={`pb-8 lg:block lg:w-[400px] lg:min-w-[400px] ${
              !useNewUI ? 'hidden' : ''
            }`}
          >
            {!useNewUI && <ProductDetails deal={deal} showDealRatings />}

            {useNewUI && (
              <>
                <PricingCard deal={deal} planTypeText={planTypeText} />

                <Button
                  className="mt-6 block text-center"
                  tertiary
                  href={`${PDPUrl}/#pricePlans`}
                >
                  <FontAwesomeIcon icon={faExternalLink} className="mr-2" />
                  View plan details
                </Button>
              </>
            )}

            {discussionType === DISCUSSION.REVIEWS && (
              <div className="hidden lg:block">
                <DealRatings deal={deal} />
                <Button
                  href={
                    isLeaveReviewPageEnable
                      ? `/products/${deal.slug}/reviews/new`
                      : `/products/${deal.slug}/#reviews`
                  }
                  disabled={
                    !userStatus?.user_has_purchased || user?.comment_blacklist
                  }
                  secondary
                >
                  Write a review
                </Button>
              </div>
            )}
          </aside>
        </article>
      </div>
    </PDPContext.Provider>
  );
}

export default AggregateLayout;
